import { api } from '@api'
import { ActiveChip as Chip, ClearAllButton } from '@components/FilterControls'
import { PageSearch, useSearchParamSync } from '@components/PageSearch'
import { useSearchParams } from '@solidjs/router'
import { useQuery } from '@tanstack/solid-query'
import { For, Show, createEffect, on } from 'solid-js'
import { useService } from 'solid-services'
import { Flex } from 'styled-system/jsx'
import { defaults, useFilters } from '../state/filter'

/** Keeps `?q=` and `?author=` in step with the setups filters */
export function useSetupsUrlSync() {
  const { fetchFilters, setSearch, setAuthor } = useService(useFilters)()
  const [params] = useSearchParams()

  useSearchParamSync(() => fetchFilters().search, setSearch)

  // `author` only flows in from the URL: a member result in global search links to /setups?author=<id>
  createEffect(
    on(
      () => params.author,
      (author) => {
        const next = typeof author === 'string' ? author : null
        if (next !== fetchFilters().author) setAuthor(next)
      },
    ),
  )
}

/** Search box shown in the header on desktop, and above the list on smaller screens */
export function SetupsSearch(props: { class?: string }) {
  const { searchInput, setSearchInput } = useService(useFilters)()
  return (
    <PageSearch
      class={props.class}
      value={searchInput()}
      onInput={setSearchInput}
      placeholder='Search title, symbol, exchange…'
    />
  )
}

/**
 * Removable chips for whatever is narrowing the list right now, so it's always clear why
 * you're seeing these setups — and one tap undoes any of it.
 */
export function ActiveFilters() {
  const { fetchFilters, applyNow, setSearch, setAuthor, clearAll, activeFilterCount } = useService(useFilters)()
  const [, setParams] = useSearchParams()

  const author = useQuery(() => ({
    queryKey: ['profile-username', fetchFilters().author],
    queryFn: async () => {
      const { data } = await api().from('profile').select('username').eq('id', fetchFilters().author!).single()
      return data?.username ?? 'member'
    },
    enabled: !!fetchFilters().author,
    staleTime: Number.POSITIVE_INFINITY,
  }))

  const f = () => fetchFilters()
  const isChanged = <K extends keyof typeof defaults>(k: K) => JSON.stringify(f()[k]) !== JSON.stringify(defaults[k])
  // Enum filters are summarised as one chip each; the drawer holds the detail
  const enumFilters = () =>
    (
      [
        ['assetType', 'Market'],
        ['position', 'Position'],
        ['stage', 'Stage'],
        ['patternSpecification', 'Pattern'],
        ['duration', 'Duration'],
      ] as const
    ).filter(([k]) => isChanged(k))

  const visible = () => !!f().search || activeFilterCount() > 0

  return (
    <Show when={visible()}>
      <Flex gap='1.5' flexWrap='wrap' alignItems='center' mb='4'>
        <Show when={f().search}>
          <Chip label='search' onRemove={() => setSearch('')}>“{f().search}”</Chip>
        </Show>
        <Show when={f().author}>
          <Chip
            label='author'
            onRemove={() => {
              setAuthor(null)
              setParams({ author: undefined }, { replace: true })
            }}
          >
            By @{author.data ?? '…'}
          </Chip>
        </Show>
        <For each={f().symbols}>
          {(symbol) => (
            <Chip label={symbol} onRemove={() => applyNow({ symbols: f().symbols.filter((s) => s !== symbol) })}>
              {symbol}
            </Chip>
          )}
        </For>
        <For each={f().exchanges}>
          {(exchange) => (
            <Chip label={exchange} onRemove={() => applyNow({ exchanges: f().exchanges.filter((s) => s !== exchange) })}>
              {exchange}
            </Chip>
          )}
        </For>
        <For each={enumFilters()}>
          {([key, label]) => (
            <Chip label={label} onRemove={() => applyNow({ [key]: defaults[key] })}>
              {label}: {(f()[key] as (string | null)[]).length} selected
            </Chip>
          )}
        </For>
        <ClearAllButton
          onClick={() => {
            clearAll()
            setParams({ q: undefined, author: undefined }, { replace: true })
          }}
        />
      </Flex>
    </Show>
  )
}
